/**
 * VendorShell — vendor top strip + portal-scoped warmth (R-1 vendor exception).
 * Contractor identity (logo/monogram) is primary; SOVIQ AI platform line is secondary.
 */
import { ReactNode, useMemo, useState, useRef, useEffect } from 'react'
import { brand } from '../../config/brand'
import { BrandWordmark } from '../brand/BrandWordmark'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bell, LogOut, Settings } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { switchPortal } from '../../lib/switchPortal'
import {
  fetchMyProjects,
  contractorLogoUrl,
  vendorLogout,
  type VendorProject,
} from '../../api/vendor'

interface VendorShellProps {
  children: ReactNode
}

function monogram(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?'
}

function pickContractor(projects: VendorProject[] | undefined) {
  if (!projects?.length) return null
  const withLogo = projects.find((p) => p.contractor_has_logo)
  const p = withLogo || projects[0]
  const company = (p.contractor_company_name || p.contractor_name || '').trim()
  if (!company) return null
  return {
    projectId: p.id,
    company,
    hasLogo: !!p.contractor_has_logo,
  }
}

function NotificationsDropdown({ onClose: _onClose }: { onClose: () => void }) {
  return (
    <div
      className="vp-notif-dd"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="vp-notif-empty">No new notifications</p>
    </div>
  )
}

export default function VendorShell({ children }: VendorShellProps) {
  const { user, refetch } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [notifOpen, setNotifOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [portalBusy, setPortalBusy] = useState(false)
  const notifRef = useRef<HTMLDivElement>(null)
  const showYourProjects = user?.has_contractor_projects === true

  async function goYourProjects() {
    if (portalBusy) return
    setPortalBusy(true)
    try {
      await switchPortal('contractor', { refetch, navigate })
    } catch {
      /* keep shell usable */
    } finally {
      setPortalBusy(false)
    }
  }

  const { data: projects } = useQuery({
    queryKey: ['vendor-my-projects'],
    queryFn: fetchMyProjects,
    staleTime: 60_000,
  })
  const contractor = useMemo(() => pickContractor(projects), [projects])

  useEffect(() => {
    if (!notifOpen) return
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [notifOpen])

  const isActive = (path: string) =>
    path === '/vendor/dashboard'
      ? pathname === path
      : pathname.startsWith(path)

  async function handleLogout() {
    if (loggingOut) return
    setLoggingOut(true)
    await vendorLogout({ loggedOutNote: true })
  }

  const vendorLabel = user?.company_name ?? user?.name ?? 'Vendor'

  return (
    <div className="vendor-portal" data-testid="vendor-portal" style={{ paddingTop: '42px' }}>
      <div id="strip" className="vp-strip">
        <div className="vp-brand-block" data-testid="vendor-portal-contractor">
          {contractor ? (
            <>
              {contractor.hasLogo ? (
                <img
                  className="vp-contractor-logo"
                  src={contractorLogoUrl(contractor.projectId)}
                  alt=""
                  data-testid="vendor-portal-logo"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none'
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement | null
                    if (fallback) fallback.style.display = 'flex'
                  }}
                />
              ) : null}
              <div
                className="vp-contractor-mono"
                data-testid="vendor-portal-monogram"
                style={{ display: contractor.hasLogo ? 'none' : 'flex' }}
                aria-hidden={contractor.hasLogo}
              >
                {monogram(contractor.company)}
              </div>
              <div className="vp-contractor-text">
                <div className="vp-contractor-name">{contractor.company}</div>
                <div className="vp-platform-line">{brand.name} · Vendor Portal</div>
              </div>
            </>
          ) : (
            <div className="brand">
              <BrandWordmark />
              <div className="vp-platform-line">Vendor Portal</div>
            </div>
          )}
        </div>

        <div className="tabs">
          <button
            className={`tab${isActive('/vendor/dashboard') ? ' on' : ''}`}
            onClick={() => navigate('/vendor/dashboard')}
          >
            <span className="tab-n">1</span>My Projects
          </button>
          <button
            className={`tab${isActive('/vendor/documents') ? ' on' : ''}`}
            onClick={() => navigate('/vendor/documents')}
          >
            <span className="tab-n">2</span>Documents
          </button>
          <button
            className={`tab${isActive('/vendor/notifications') ? ' on' : ''}`}
            onClick={() => navigate('/vendor/notifications')}
          >
            <span className="tab-n">3</span>Notifications
          </button>
          <button
            className={`tab${isActive('/vendor/profile') ? ' on' : ''}`}
            style={{
              marginLeft: '14px',
              paddingLeft: '14px',
              borderLeft: '1px solid var(--line)',
              color: isActive('/vendor/profile') ? 'var(--ink)' : 'var(--ink4)',
            }}
            onClick={() => navigate('/vendor/profile')}
          >
            <Settings size={12} style={{ marginRight: 4 }} />
            Profile
          </button>
        </div>

        <div className="strip-r">
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button
              className="icon-btn"
              onClick={() => setNotifOpen((v) => !v)}
              aria-label="Notifications"
            >
              <Bell size={15} />
            </button>
            {notifOpen && <NotificationsDropdown onClose={() => setNotifOpen(false)} />}
          </div>

          <span className="vp-user-chip" title={vendorLabel}>{vendorLabel}</span>

          {showYourProjects && (
            <button
              type="button"
              className="btn b-ol"
              data-testid="vendor-switch-projects"
              disabled={portalBusy}
              onClick={() => { void goYourProjects() }}
              style={{ fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }}
            >
              {portalBusy ? 'Opening…' : 'Your projects'}
            </button>
          )}

          <button
            type="button"
            onClick={() => void handleLogout()}
            className="icon-btn vp-logout-btn"
            aria-label="Log out"
            title="Log out"
            data-testid="vendor-logout"
            disabled={loggingOut}
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>

      <div className="shell">
        <div className="main">
          <main className="content">{children}</main>
        </div>
      </div>
    </div>
  )
}
